import qrcode from 'qrcode-generator';
import { h } from '../lib/dom.js';
import { getState, CONFIG, isGaslessCapable } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import { t } from '../i18n/index.js';
import {
  setReceiveAccount, setReceiveCcy, setReceiveChain,
  copyText, ensureReceiveAddress, toggleReceiveDetails, openOpenAccount, openInNewWindow, NATIVE_CCY
} from '../actions.js';
import { buildPaymentUri } from '../lib/paymentUri.js';
import { renderActivityRail } from './activityRail.js';

// Generated fresh at render time from the (worker-derived) address string —
// never cached in state, since it's a pure, cheap synchronous function of
// that string. createDataURL() returns a data: URL image, not a markup
// string, so this never goes anywhere near innerHTML/the html: attr (that
// path is reserved for our own static icon SVGs — CLAUDE.md forbids
// injecting network/worker-derived data through it). Fixed type-number 0
// (auto-size) and 'M' error correction — reasonable defaults for a short
// address string.
function qrDataUrl(text) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  return qr.createDataURL(6, 8);
}

// ensureReceiveAddress is triggered from the field-change actions
// (setReceiveAccount/Ccy/Chain) and goReceive — never from render itself,
// which would cause reentrant renders (see the comment in actions.js).
export function renderReceive() {
  const s = getState();
  // No accounts yet (item 10 dropped the seeded demo wallets) — the
  // account-select/balance lookups below all assume at least one exists.
  if (s.wallets.length === 0) {
    return h('div', { class: 'screen' }, [
      h('h1', { class: 'screen-title' }, t('receive.title')),
      h('div', { class: 'empty-state' }, t('receive.noAccountsEmpty')),
      h('button', { class: 'btn btn-primary', onclick: openOpenAccount }, t('receive.openAccount'))
    ]);
  }
  const r = s.receive;

  // Every currency this app supports receiving, not just whatever this
  // particular account already happens to hold a balance in (a fresh
  // account only ever starts with USDC — receiving is exactly how you'd
  // first acquire a balance in anything else, so restricting the list to
  // existing balances made currencies like USDT unreachable here, and
  // Tron along with it, since it's only ever reachable via a USDT/CNHT
  // selection). No reporting-currency option at all: there's no on-chain
  // address for fiat.
  const ccyOptions = Object.keys(CONFIG.CHAINS);
  const chainOptions = CONFIG.CHAINS[r.ccy] || [];

  const accountSelect = h('select', { onchange: (e) => setReceiveAccount(e.target.value) },
    s.wallets.map((w) => h('option', { value: w.id, selected: w.id === r.accountId || undefined }, w.name)));

  const ccySelect = h('select', { onchange: (e) => setReceiveCcy(e.target.value) },
    ccyOptions.map((c) => h('option', { value: c, selected: c === r.ccy || undefined }, c)));

  const detailsToggle = h('button', { class: 'btn btn-secondary btn-sm full-width', onclick: toggleReceiveDetails },
    r.showDetails ? t('receive.hideDetails') : t('receive.showDetails'));

  let cryptoCard = null;
  if (r.showDetails) {
    const chainSelect = h('select', { onchange: (e) => { setReceiveChain(e.target.value); } },
      chainOptions.map((c) => h('option', { value: c, selected: c === (r.chain || chainOptions[0]) || undefined }, c)));

    let addressArea;
    if (r.addressLoading) {
      addressArea = h('div', { class: 'address-loading' }, [
        h('div', { class: 'spinner' }),
        h('div', { class: 'text-muted' }, t('receive.generatingAddress'))
      ]);
    } else if (r.addressError) {
      addressArea = h('div', { class: 'stack items-center' }, [
        h('div', { class: 'field-error' }, r.addressError),
        h('button', { class: 'btn btn-secondary btn-sm', onclick: ensureReceiveAddress }, t('common.retry'))
      ]);
    } else {
      // EIP-681-style URI for EVM / the tron: convention for Tron
      // (feature-harden.md section 3) -- lets a scanning wallet
      // recognize which chain the address belongs to, instead of
      // guessing from a bare string. The displayed/copied address text
      // below stays the raw address either way.
      const effectiveChain = r.chain || chainOptions[0];
      const qrImage = h('div', { class: 'qr-image-wrap' }, [
        h('img', {
          class: 'qr-image',
          src: qrDataUrl(buildPaymentUri(effectiveChain, r.address || '', s.testnetEnabled)),
          alt: t('receive.qrAlt', { address: r.address || '' })
        })
      ]);
      addressArea = h('div', { class: 'stack items-center' }, [
        qrImage,
        h('div', { class: 'flex-gap-sm items-center' }, [
          h('div', { class: 'mono receive-address' }, r.address || ' '),
          h('button', {
            class: 'icon-btn-plain', 'aria-label': t('receive.copyAddress'), onclick: () => copyText(r.address || '', t('common.address'), 'address')
          }, s.copyFlash === 'address'
            ? h('span', { html: iconMarkup('check', { size: 16 }) })
            : h('span', { html: iconMarkup('clipboardCopy', { size: 16 }) }))
        ]),
        // feature-outstanding.md (2026-08-07): this address is a real,
        // different on-chain address for gasless-capable pairs (a GasFree
        // contract address / ERC-4337 smart account — see
        // ensureReceiveAddress's gasless param) — explained right here
        // rather than left as an unexplained address change.
        isGaslessCapable(r.ccy, effectiveChain)
          ? h('div', { class: 'text-sm text-muted' }, t('receive.gaslessNote', { ccy: r.ccy, native: NATIVE_CCY[effectiveChain] || '' }))
          : null
      ]);
    }

    cryptoCard = h('div', { class: 'card card-pad-lg stack items-center' }, [
      h('div', { class: 'full-width' }, [
        h('div', { class: 'card-label' }, t('receive.network')),
        chainSelect
      ]),
      addressArea
    ]);
  }

  // Desktop-only rail (renderActivityRail(false) — see activityRail.js and
  // style.css's .screen-rail default display:none) — Receive had no
  // recent-activity list at all pre-Desktop, unlike Home.
  return h('div', { class: 'screen-layout' }, [
    h('div', { class: 'screen screen-main' }, [
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('h1', { class: 'screen-title' }, t('receive.title')),
        h('button', {
          class: 'icon-btn-plain desktop-only-icon', 'aria-label': t('nav.openInNewWindow'), onclick: () => openInNewWindow('receive')
        }, h('span', { html: iconMarkup('squareArrowOutUpRight', { size: 15, color: '#757993' }) }))
      ]),
      h('div', {}, [
        h('div', { class: 'card-label' }, t('receive.receivingAccount')),
        accountSelect
      ]),
      h('div', {}, [
        h('div', { class: 'card-label' }, t('receive.receivingCurrency')),
        ccySelect
      ]),
      detailsToggle,
      cryptoCard
    ]),
    renderActivityRail(false)
  ]);
}
